// Format currency
export const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

// Format date
export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat("en-US", defaultOptions).format(
    new Date(date)
  );
};

// UTC 시간을 한국 시간으로 변환
export const convertUTCToKoreaTime = (utcDateString) => {
  if (!utcDateString) return null;

  const utcDate = new Date(utcDateString);
  const koreaTime = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000); // UTC+9
  return koreaTime;
};

// 한국 시간을 UTC로 변환
export const convertKoreaTimeToUTC = (koreaDate) => {
  if (!koreaDate) return null;

  const date = new Date(koreaDate);

  // 한국 시간대 정보가 있는 경우, 정확한 UTC 변환
  const isKoreaTime =
    date.toString().includes("GMT+0900") ||
    date.toString().includes("한국 표준시");

  if (isKoreaTime) {
    // 한국 시간에서 9시간을 빼서 UTC로 변환
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // 한국 시간에서 9시간을 빼서 UTC로 변환
    const utcHours = hours - 9;

    // 시간이 음수가 되면 전날로 이동
    if (utcHours < 0) {
      const prevDay = new Date(year, month, day - 1);
      const utcDate = new Date(
        prevDay.getFullYear(),
        prevDay.getMonth(),
        prevDay.getDate(),
        utcHours + 24,
        minutes,
        seconds
      );

      return utcDate;
    } else {
      // UTC 시간을 직접 생성 (로컬 시간대 변환 방지)
      const utcDate = new Date(
        Date.UTC(year, month, day, utcHours, minutes, seconds)
      );

      return utcDate;
    }
  } else {
    // 일반적인 변환
    const utcTime = new Date(date.getTime() - 9 * 60 * 60 * 1000);

    // console.log("🔄 한국시간 → UTC 변환:", {
    //   koreaTime: date.toString(),
    //   utcTime: utcTime.toISOString(),
    // });

    return utcTime;
  }
};

// 한국 시간 기준으로 날짜 문자열 반환 (YYYY-MM-DD 형식)
export const getKoreaDateString = (date = new Date()) => {
  const koreaTime = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return koreaTime.toISOString().split("T")[0];
};

// 한국 시간 기준으로 내일 날짜 반환
export const getKoreaTomorrowDate = () => {
  const now = new Date();
  const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const tomorrow = new Date(koreaTime);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function
export const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Generate random ID
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

// Validate email
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Capitalize first letter
export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + "...";
};

// 참여 현황 계산 함수 (퍼센트, 소수점 절사)
export const calculateParticipationStatus = (ad) => {
  if (!ad || !ad.totalQuantity) {
    return "0%";
  }

  const totalQuantity = ad.totalQuantity;

  // PENDING 상태일 때
  if (ad.status === "PENDING") {
    return "0%";
  }

  // DONE 상태일 때
  if (ad.status === "DONE") {
    return "100%";
  }

  // APPROVED 상태일 때: startDate와 endDate를 계산하여 현재 시간에 비례한 참여율 계산
  if (ad.status === "APPROVED") {
    if (!ad.startDate || !ad.endDate) {
      return "0%";
    }

    // startDate와 endDate는 UTC이므로 한국 시간으로 변환
    const startDate = convertUTCToKoreaTime(ad.startDate);
    const endDate = convertUTCToKoreaTime(ad.endDate);

    // 현재 시간은 한국 시간 (로컬 시간 + 9시간)
    const now = new Date();
    const koreaNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);

    if (!startDate || !endDate) {
      return "0%";
    }

    // 전체 기간 계산 (밀리초) - 모두 한국 시간 기준
    const totalDuration = endDate.getTime() - startDate.getTime();

    // 현재까지 경과 시간 계산 (밀리초) - 모두 한국 시간 기준
    const elapsedDuration = Math.max(
      0,
      koreaNow.getTime() - startDate.getTime()
    );

    // 전체 기간이 0이거나 음수인 경우 처리
    if (totalDuration <= 0) {
      return "0%";
    }

    // 경과 시간이 전체 기간을 초과하는 경우
    if (elapsedDuration >= totalDuration) {
      return "100%";
    }

    // 비례 계산 후 소수점 절사: floor((경과시간 / 전체기간) * 100)
    const percent = Math.floor((elapsedDuration / totalDuration) * 100);

    return `${percent}%`;
  }

  // 기타 상태일 때
  return "0%";
};

/** 외부 URL을 referrer 없이 새 탭으로 연다 (유입 경로 노출 방지) */
export const openExternalUrl = (url) => {
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.referrerPolicy = "no-referrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};
